import React from "react";
import Button from "../../../components/Form/Button";
import { useFormik, FormikProvider, Field } from "formik";
import Input from "../../../components/Form/Input";
import Select from "../../../components/Form/Select";
import * as Yup from "yup";
import { createProgramApi } from "../../../apis/apis";
import { pushToast } from "../../../components/Toaster/Toaster.slice";
import { useAppDispatch } from "../../../shared/redux/hooks";
import { CreateProgramBody } from "../../../apis/types";

// Wrapper for date inputs to handle styling conflicts with error icons
const DateInput: React.FC<any> = (props) => {
  const { error, ...rest } = props;
  return (
    <div className="relative">
      <Input
        {...rest}
        error={error}
        style={error ? { paddingRight: '3.5rem' } : {}}
      />
    </div>
  );
};

interface CreateProgramFormProps {
  closeFunction: () => void;
  onSuccess?: () => void;
  properties: Array<{
    id: string;
    nameLocation: string;
    name: string;
    location: string;
  }>;
}

const CreateProgramForm: React.FC<CreateProgramFormProps> = ({
  closeFunction,
  onSuccess,
  properties
}) => {
  const dispatch = useAppDispatch();

  const programTypeOptions = {
    NEM2V: 'NEM2V',
    MASTER_METER: 'Master Meter',
    PASSTHRU: 'Passthru'
  };

  const propertyOptions = properties.reduce((acc, property) => {
    acc[property.id] = property.nameLocation;
    return acc;
  }, { '': 'Select a property...' } as Record<string, string>);

  const requiredMessage = 'Required';

  const formik = useFormik<CreateProgramBody>({
    initialValues: {
      type: 'NEM2V',
      startDate: '',
      endDate: '',
      billingDelayCycles: 1,
      billingOn: false,
      propertyCuid: '',
      billingOnDate: '',
      ptoDate: '',
      name: '',
      salesforceId: '',
    },
    validationSchema: Yup.object({
      type: Yup.string().required(requiredMessage),
      startDate: Yup.string().required(requiredMessage),
      endDate: Yup.string(),
      billingDelayCycles: Yup.number()
        .min(1, 'Billing delay cycles must be at least 1')
        .required(requiredMessage),
      billingOn: Yup.boolean(),
      propertyCuid: Yup.string().required(requiredMessage),
      billingOnDate: Yup.string().required(requiredMessage),
      ptoDate: Yup.string().required(requiredMessage),
      name: Yup.string(),
      salesforceId: Yup.string(),
    }),
    validateOnChange: false,
    validateOnBlur: false,
    onSubmit: async (values, { setSubmitting, validateForm }) => {
      // Trigger validation manually on submit
      const errors = await validateForm(values);

      if (Object.keys(errors).length === 0) {
        setSubmitting(true);
        try {
          await createProgramApi(values);
          await dispatch(
            pushToast({
              message: 'Program created successfully',
              type: 'success',
            })
          );
          onSuccess?.();
          closeFunction();
        } catch (e: any) {
          await dispatch(
            pushToast({
              message: 'Failed to create program',
              type: 'error',
              description: `${e.response?.data?.error !== undefined ? `${e.response?.data?.error as string}:` : 'Error:'} ${e.message as string}`,
            })
          );
        } finally {
          setSubmitting(false);
        }
      }
    },
  });

  return (
    <div className="bg-white dark:bg-slate-800 px-5 py-4 w-full max-w-screen-lg h-full mx-auto shadow-xl rounded-2xl flex flex-col">
      <div className="justify-start items-start">
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit}>
            <div className="flex flex-col justify-start items-start">
              <h2 className="mb-5 text-2xl font-medium dark:text-white">Create Program</h2>
              <div className="grid grid-cols-2 gap-4 w-full">
                <div>
                  <Field
                    label="Property"
                    id="propertyCuid"
                    name="propertyCuid"
                    component={Select}
                    options={propertyOptions}
                    required
                    error={formik.errors.propertyCuid}
                    className="w-full"
                  />
                  <Field
                    label="Program Type"
                    id="type"
                    name="type"
                    component={Select}
                    options={programTypeOptions}
                    required
                    error={formik.errors.type}
                    className="w-full mt-4"
                  />
                  <Field
                    label="Program Name"
                    id="name"
                    name="name"
                    type="text"
                    as={Input}
                    error={formik.errors.name}
                    className="w-full mt-4"
                    placeholder="Optional"
                  />
                  <Field
                    label="Billing Delay Cycles"
                    id="billingDelayCycles"
                    name="billingDelayCycles"
                    type="number"
                    as={Input}
                    required
                    error={formik.errors.billingDelayCycles}
                    className="w-full mt-4"
                  />
                  <Field
                    label="Salesforce ID"
                    id="salesforceId"
                    name="salesforceId"
                    type="text"
                    as={Input}
                    error={formik.errors.salesforceId}
                    className="w-full mt-4"
                    placeholder="Optional"
                  />
                </div>
                <div>
                  <Field
                    label="Start Date"
                    id="startDate"
                    name="startDate"
                    type="date"
                    as={DateInput}
                    required
                    error={formik.errors.startDate}
                    className="w-full"
                  />
                  <Field
                    label="End Date"
                    id="endDate"
                    name="endDate"
                    type="date"
                    as={DateInput}
                    error={formik.errors.endDate}
                    className="w-full mt-4"
                    placeholder="Optional"
                  />
                  <Field
                    label="PTO Date"
                    id="ptoDate"
                    name="ptoDate"
                    type="date"
                    as={DateInput}
                    required
                    error={formik.errors.ptoDate}
                    className="w-full mt-4"
                  />
                  <Field
                    label="VNEM Transition Start Date"
                    id="billingOnDate"
                    name="billingOnDate"
                    type="date"
                    as={DateInput}
                    required
                    error={formik.errors.billingOnDate}
                    className="w-full mt-4"
                  />
                  <div className="mt-4">
                    <label className="flex items-center">
                      <Field
                        type="checkbox"
                        name="billingOn"
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
                        VNEM Billing Active
                      </span>
                    </label>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end mt-6">
              <Button
                onClick={() => {
                  closeFunction();
                }}
                type="button"
                color="slate"
                className="w-28"
              >
                Cancel
              </Button>
              <div className="ml-2">
                <Button type="submit" isSaving={formik.isSubmitting} tabIndex={-1} className="w-28">
                  Create
                </Button>
              </div>
            </div>
          </form>
        </FormikProvider>
      </div>
    </div>
  );
};

export default CreateProgramForm;